import { useEffect, useState } from 'react';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhAgentOverviewData,
  WazuhEndpointActionRequiredItem,
  WazuhMostVulnerableEndpointItem,
  WazuhOrganizationAttentionItem,
  WazuhPagedData,
  WazuhPortfolioHealthResellerData,
  WazuhPortfolioHealthSubOrgData,
  WazuhRecentIncidentItem,
  WazuhSecurityScoreData,
} from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { applyProfileResellerScope } from './wazuhScopeParams';

type WazuhOverviewCommonState = {
  loading: boolean;
  agentOverview: WazuhAgentOverviewData | null;
  securityScore: WazuhSecurityScoreData | null;
  organizationsAttention: WazuhPagedData<WazuhOrganizationAttentionItem> | null;
  endpointsActionRequired: WazuhPagedData<WazuhEndpointActionRequiredItem> | null;
  recentIncidents: WazuhPagedData<WazuhRecentIncidentItem> | null;
};

export type WazuhResellerOverviewState = WazuhOverviewCommonState & {
  portfolioHealth: WazuhPortfolioHealthResellerData | null;
};

export type WazuhSubOrgOverviewState = WazuhOverviewCommonState & {
  portfolioHealth: WazuhPortfolioHealthSubOrgData | null;
  mostVulnerableEndpoints: WazuhPagedData<WazuhMostVulnerableEndpointItem> | null;
};

const emptyCommonState = (): WazuhOverviewCommonState => ({
  loading: true,
  agentOverview: null,
  securityScore: null,
  organizationsAttention: null,
  endpointsActionRequired: null,
  recentIncidents: null,
});

export type OverviewListPages = {
  attentionPage?: number;
  endpointsPage?: number;
  incidentsPage?: number;
  vulnerablePage?: number;
  recommendedPage?: number;
};

export const useWazuhResellerOverviewData = (
  pageSize = 5,
  pages: OverviewListPages = {},
): WazuhResellerOverviewState => {
  const { scope } = useWazuhScope();
  const { userData } = useUserProfile();
  const { wait, isPreviewMode, getPreviewState } = useEdrDataGate('reseller-dashboard');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'reseller-dashboard');
  const canPortfolio = canWidget('portfolioHealth');
  const canSecurityScore = canWidget('securityScore');
  const canClientsAttention = canWidget('clientsRequiringAttention');
  const canEndpointsAction = canWidget('endpointsRequiringAction');
  const canRecentIncidents = canWidget('recentCriticalIncidents');
  const profileOrgId = userData?.organization?.id;

  const attentionPage = pages.attentionPage ?? 0;
  const endpointsPage = pages.endpointsPage ?? 0;
  const incidentsPage = pages.incidentsPage ?? 0;
  const [state, setState] = useState<WazuhResellerOverviewState>({
    ...emptyCommonState(),
    portfolioHealth: null,
  });

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as WazuhResellerOverviewState);
      return undefined;
    }

    let cancelled = false;

    if (!profileOrgId) {
      return undefined;
    }

    const scopeWithReseller = applyProfileResellerScope(scope, profileOrgId);
    const fetches: Promise<void>[] = [];

    if (canPortfolio) {
      fetches.push(
        wazuhAPI.dashboard.getPortfolioHealthReseller(scopeWithReseller).then((portfolioHealth) => {
          if (!cancelled) setState((prev) => ({ ...prev, portfolioHealth }));
        }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, portfolioHealth: null }));
    }

    if (canSecurityScore) {
      fetches.push(
        wazuhAPI.dashboard.getSecurityScore(scopeWithReseller).then((securityScore) => {
          if (!cancelled) setState((prev) => ({ ...prev, securityScore }));
        }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, securityScore: null }));
    }

    if (canClientsAttention) {
      fetches.push(
        wazuhAPI.dashboard
          .getOrganizationsAttention({
            ...scopeWithReseller,
            page: attentionPage,
            pageSize,
          })
          .then((organizationsAttention) => {
            if (!cancelled) setState((prev) => ({ ...prev, organizationsAttention }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, organizationsAttention: null }));
    }

    if (canEndpointsAction) {
      fetches.push(
        wazuhAPI.dashboard
          .getEndpointsActionRequired({
            ...scopeWithReseller,
            page: endpointsPage,
            pageSize,
          })
          .then((endpointsActionRequired) => {
            if (!cancelled) setState((prev) => ({ ...prev, endpointsActionRequired }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, endpointsActionRequired: null }));
    }

    if (canRecentIncidents) {
      fetches.push(
        wazuhAPI.dashboard
          .getRecentIncidents({
            ...scopeWithReseller,
            page: incidentsPage,
            pageSize,
          })
          .then((recentIncidents) => {
            if (!cancelled) setState((prev) => ({ ...prev, recentIncidents }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, recentIncidents: null }));
    }

    if (fetches.length === 0) {
      setState({
        ...emptyCommonState(),
        loading: false,
        portfolioHealth: null,
      });
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true }));
    Promise.all(fetches)
      .catch((error) => {
        if (!cancelled) logger(error, 'error', 'useWazuhResellerOverviewData');
      })
      .finally(() => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    pageSize,
    attentionPage,
    endpointsPage,
    incidentsPage,
    isPreviewMode,
    wait,
    getPreviewState,
    canPortfolio,
    canSecurityScore,
    canClientsAttention,
    canEndpointsAction,
    canRecentIncidents,
  ]);

  return state;
};

export const useWazuhSubOrgOverviewData = (
  pageSize = 5,
  pages: OverviewListPages = {},
): WazuhSubOrgOverviewState => {
  const { scope } = useWazuhScope();
  const { wait, isPreviewMode, getPreviewState } = useEdrDataGate('sub-org-dashboard');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'sub-org-dashboard');
  const canPortfolio = canWidget('portfolioHealth');
  const canSecurityScore = canWidget('securityScore');
  const canRecommendedActions = canWidget('recommendedActions');
  const canEndpointsAction = canWidget('endpointsRequiringAction');
  const canRecentIncidents = canWidget('recentCriticalIncidents');
  const canMostVulnerable = canWidget('mostVulnerableEndpoints');
  const canEndpointHealth = canWidget('endpointHealth');

  const recommendedPage = pages.recommendedPage ?? 0;
  const endpointsPage = pages.endpointsPage ?? 0;
  const incidentsPage = pages.incidentsPage ?? 0;
  const vulnerablePage = pages.vulnerablePage ?? 0;

  const [state, setState] = useState<WazuhSubOrgOverviewState>({
    ...emptyCommonState(),
    portfolioHealth: null,
    mostVulnerableEndpoints: null,
  });

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as WazuhSubOrgOverviewState);
      return undefined;
    }

    let cancelled = false;
    const fetches: Promise<void>[] = [];

    if (canPortfolio) {
      fetches.push(
        wazuhAPI.dashboard.getPortfolioHealthSubOrg(scope).then((portfolioHealth) => {
          if (!cancelled) setState((prev) => ({ ...prev, portfolioHealth }));
        }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, portfolioHealth: null }));
    }

    if (canSecurityScore) {
      fetches.push(
        wazuhAPI.dashboard.getSecurityScore(scope).then((securityScore) => {
          if (!cancelled) setState((prev) => ({ ...prev, securityScore }));
        }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, securityScore: null }));
    }

    if (canRecommendedActions) {
      fetches.push(
        wazuhAPI.dashboard
          .getOrganizationsAttention({ ...scope, page: recommendedPage, pageSize })
          .then((organizationsAttention) => {
            if (!cancelled) setState((prev) => ({ ...prev, organizationsAttention }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, organizationsAttention: null }));
    }

    if (canEndpointsAction) {
      fetches.push(
        wazuhAPI.dashboard
          .getEndpointsActionRequired({ ...scope, page: endpointsPage, pageSize })
          .then((endpointsActionRequired) => {
            if (!cancelled) setState((prev) => ({ ...prev, endpointsActionRequired }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, endpointsActionRequired: null }));
    }

    if (canRecentIncidents) {
      fetches.push(
        wazuhAPI.dashboard
          .getRecentIncidents({ ...scope, page: incidentsPage, pageSize })
          .then((recentIncidents) => {
            if (!cancelled) setState((prev) => ({ ...prev, recentIncidents }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, recentIncidents: null }));
    }

    if (canMostVulnerable) {
      fetches.push(
        wazuhAPI.dashboard
          .getMostVulnerableEndpoints({ ...scope, page: vulnerablePage, pageSize })
          .then((mostVulnerableEndpoints) => {
            if (!cancelled) setState((prev) => ({ ...prev, mostVulnerableEndpoints }));
          }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, mostVulnerableEndpoints: null }));
    }

    if (canEndpointHealth) {
      fetches.push(
        wazuhAPI.dashboard.getAgentOverview(scope).then((agentOverview) => {
          if (!cancelled) setState((prev) => ({ ...prev, agentOverview }));
        }),
      );
    } else if (!cancelled) {
      setState((prev) => ({ ...prev, agentOverview: null }));
    }

    if (fetches.length === 0) {
      setState({
        ...emptyCommonState(),
        loading: false,
        portfolioHealth: null,
        mostVulnerableEndpoints: null,
      });
      return undefined;
    }

    setState((prev) => ({ ...prev, loading: true }));
    Promise.all(fetches)
      .catch((error) => {
        if (!cancelled) logger(error, 'error', 'useWazuhSubOrgOverviewData');
      })
      .finally(() => {
        if (!cancelled) setState((prev) => ({ ...prev, loading: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    pageSize,
    recommendedPage,
    endpointsPage,
    incidentsPage,
    vulnerablePage,
    isPreviewMode,
    wait,
    getPreviewState,
    canPortfolio,
    canSecurityScore,
    canRecommendedActions,
    canEndpointsAction,
    canRecentIncidents,
    canMostVulnerable,
    canEndpointHealth,
  ]);

  return state;
};

/** @deprecated Use useWazuhResellerOverviewData or useWazuhSubOrgOverviewData */
export const useWazuhOverviewData = useWazuhResellerOverviewData;
